import type { UrlObject } from 'node:url'

export const resolvePath = (pathname: string | null, query?: Record<string, string | number | boolean>): string => {
    const basePath = `${pathname}`

    if (!query) return basePath

    const queryString = Object.entries(query)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')

    return queryString ? `${basePath}?${queryString}` : basePath
}

export const resolveRoute = (pathname: string | null): string | null => {
    if (pathname?.includes('/loans/')) return 'home'
    if (pathname?.includes('/subscriptions/')) return 'subscriptions'
    if (pathname?.includes('/invocations/')) return 'invocations'
    if (pathname?.includes('/users/')) return 'user'
    if (pathname?.includes('/new-user')) return 'newUser'
    if (pathname?.includes('/create-loans/')) return 'createLoans'

    switch (pathname) {
        case '/':
            return 'home'
        case '/repayments':
            return 'repayments'
        case '/credits':
            return 'credits'
        case '/users':
            return 'users'
        case '/new-advance':
            return 'newAdvance'
        case '/create-invoice':
            return 'createLoans'
        case '/new-credit':
            return 'newCredit'
        case '/statistics':
            return 'statistics'
        case '/settings':
            return 'settings'
        case '/developer':
        case '/webhooks':
            return 'developer'
        case '/login':
            return 'login'
        case '/kyc':
            return 'kyc'

        case null:
        default:
            return null
    }
}

const rootRouteTitle = 'home'
const routeTitleMap: { [key: string]: string | null } = {
    '/loans': rootRouteTitle,
    '/repayments': rootRouteTitle,
    '/debtors': 'counterparties',
    '/users': 'counterparties',
    '/settings': 'settings',
    '/limits': 'limits',
    '/analytics': 'analytics',
    '/receivables': 'receivables',
    '/invoices': 'invoices',
    '/event-simulator': 'developers',
    '/webhooks': 'developers',
    '/authentication': 'developers',
    '/billing': 'billing',
    '/quotes': 'quotes',
    default: null
}

export const resolveNewRoute = (pathname: string | null): string | null => {
    if (pathname === '/') return rootRouteTitle

    const matchingRoute = Object.keys(routeTitleMap).find((route) => pathname?.includes(route))
    return matchingRoute ? routeTitleMap[matchingRoute] : routeTitleMap.default
}

export const hasSameDomain = (urlString: string | UrlObject): boolean => {
    try {
        const currentDomain = window.location.hostname

        let urlToTest: string

        if (typeof urlString === 'string') {
            urlToTest = urlString
        } else {
            urlToTest = urlString.href || urlString.pathname || ''
        }

        const parsedUrl = new URL(urlToTest)
        return parsedUrl.hostname === currentDomain
    } catch (error) {
        // If URL parsing fails, assume it's not a valid external URL
        return true
    }
}

const WEB_PROTOCOLS = ['https:', 'http:']

/*
 * SEC-5. A URL from the API becomes an href only when it is a web address, so a `javascript:` or a
 * `data:` value never runs. The hosts vary by provider, so there is no host list to check.
 */
export const isWebUrl = (value: string): boolean => {
    try {
        return WEB_PROTOCOLS.includes(new URL(value).protocol)
    } catch {
        return false
    }
}
